import { useCallback, useMemo, useRef, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  IconButton,
  Paper,
  Snackbar,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Tooltip,
  Typography,
} from '@mui/material';
import UploadFileIcon from '@mui/icons-material/UploadFile';
import DeleteIcon from '@mui/icons-material/Delete';
import CloudUploadIcon from '@mui/icons-material/CloudUpload';
import DashboardLayout from '../components/DashboardLayout';
import { api, buildDropshipPriorityRequestBody, endpoints, parseDropshipPriorityResponse } from '../api/config';

const ACCEPTED_TYPES = ['.csv', 'text/csv', 'application/vnd.ms-excel'];

const normalizeHeader = (value) =>
  String(value || '')
    .trim()
    .toLowerCase()
    .replace(/[\s-]+/g, '_');

const PRIORITY_HEADERS = new Set(['priority', 'prio', 'order_priority']);
const INTERNAL_ID_HEADERS = new Set([
  'internal_id',
  'sales_order_internal_id',
  'so_internal_id',
  'order_internal_id',
]);

const buildUpdatePayload = (parsedRows) => {
  const duplicateInternalIds = [];
  const seen = new Set();
  const payload = [];

  parsedRows.forEach((row) => {
    if (seen.has(row.internal_id)) {
      if (!duplicateInternalIds.includes(row.internal_id)) {
        duplicateInternalIds.push(row.internal_id);
      }
      return;
    }
    seen.add(row.internal_id);
    payload.push({ internal_id: row.internal_id, priority: row.priority });
  });

  return { payload, duplicateInternalIds };
};

const parseCsvLine = (line) => {
  const values = [];
  let current = '';
  let inQuotes = false;

  for (let i = 0; i < line.length; i += 1) {
    const char = line[i];
    if (char === '"') {
      if (inQuotes && line[i + 1] === '"') {
        current += '"';
        i += 1;
      } else {
        inQuotes = !inQuotes;
      }
      continue;
    }
    if (char === ',' && !inQuotes) {
      values.push(current.trim());
      current = '';
      continue;
    }
    current += char;
  }
  values.push(current.trim());
  return values;
};

const parseCsvText = (text) => {
  const lines = text
    .replace(/^\uFEFF/, '')
    .split(/\r?\n/)
    .map((line) => line.trim())
    .filter(Boolean);

  if (lines.length < 2) {
    throw new Error('CSV must include a header row and at least one data row.');
  }

  const headers = parseCsvLine(lines[0]).map(normalizeHeader);
  const internalIdIndex = headers.findIndex((header) => INTERNAL_ID_HEADERS.has(header));
  const priorityIndex = headers.findIndex((header) => PRIORITY_HEADERS.has(header));

  if (internalIdIndex === -1) {
    throw new Error('Missing internal_id column.');
  }
  if (priorityIndex === -1) {
    throw new Error('Missing priority column.');
  }

  const rows = [];
  const errors = [];

  lines.slice(1).forEach((line, lineIndex) => {
    const values = parseCsvLine(line);
    const internal_id = String(values[internalIdIndex] ?? '').trim();
    const priorityRaw = String(values[priorityIndex] ?? '').trim();
    const priority = Number(priorityRaw);

    if (!internal_id) {
      errors.push(`Row ${lineIndex + 2}: internal_id is required.`);
      return;
    }
    if (!Number.isFinite(priority)) {
      errors.push(`Row ${lineIndex + 2}: priority must be a number.`);
      return;
    }

    rows.push({ internal_id, priority });
  });

  if (!rows.length) {
    throw new Error('No valid rows found in CSV.');
  }

  return { rows, errors };
};

const BulkSalesOrderPriority = () => {
  const inputRef = useRef(null);
  const [file, setFile] = useState(null);
  const [parsedRows, setParsedRows] = useState([]);
  const [parseWarnings, setParseWarnings] = useState([]);
  const [isDragging, setIsDragging] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });
  const [submitResult, setSubmitResult] = useState(null);

  const previewRows = useMemo(
    () =>
      parsedRows.map((row, index) => ({
        ...row,
        id: `${row.internal_id}-${index}`,
      })),
    [parsedRows]
  );

  const resetSelection = useCallback(() => {
    setFile(null);
    setParsedRows([]);
    setParseWarnings([]);
    setSubmitResult(null);
  }, []);

  const loadFile = useCallback(
    async (selectedFile) => {
      if (!selectedFile) return;

      const fileName = selectedFile.name.toLowerCase();
      const isCsv =
        fileName.endsWith('.csv') ||
        ACCEPTED_TYPES.includes(selectedFile.type) ||
        !selectedFile.type;

      if (!isCsv) {
        setSnackbar({ open: true, message: 'Please upload a CSV file.', severity: 'warning' });
        return;
      }

      try {
        const text = await selectedFile.text();
        const { rows, errors } = parseCsvText(text);
        setFile(selectedFile);
        setParsedRows(rows);
        setParseWarnings(errors);
        setSnackbar({
          open: true,
          message: `Loaded ${rows.length} row(s) from ${selectedFile.name}.`,
          severity: errors.length ? 'warning' : 'success',
        });
      } catch (err) {
        resetSelection();
        setSnackbar({
          open: true,
          message: err?.message || 'Failed to parse CSV file.',
          severity: 'error',
        });
      }
    },
    [resetSelection]
  );

  const handleInputChange = useCallback(
    (event) => {
      const selected = event.target.files?.[0];
      if (selected) loadFile(selected);
      event.target.value = '';
    },
    [loadFile]
  );

  const handleDrop = useCallback(
    (event) => {
      event.preventDefault();
      event.stopPropagation();
      setIsDragging(false);
      const dropped = event.dataTransfer?.files?.[0];
      if (dropped) loadFile(dropped);
    },
    [loadFile]
  );

  const handleDownloadSampleCsv = useCallback(() => {
    const sampleCsv = ['internal_id,priority', 'SO12345,10', 'SO67890,5'].join('\n');
    const blob = new Blob([sampleCsv], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.setAttribute('download', 'bulk-sales-order-priority-sample.csv');
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
  }, []);

  const handleSubmit = async () => {
    if (!parsedRows.length) {
      setSnackbar({ open: true, message: 'Upload a CSV file before submitting.', severity: 'warning' });
      return;
    }

    setIsSubmitting(true);
    setSubmitResult(null);
    try {
      const { payload, duplicateInternalIds } = buildUpdatePayload(parsedRows);

      if (duplicateInternalIds.length > 0) {
        throw new Error(
          `Duplicate internal_id in CSV: ${duplicateInternalIds.slice(0, 10).join(', ')}.`
        );
      }

      const response = await api.post(
        endpoints.dropshipUpdatePriority,
        buildDropshipPriorityRequestBody(payload)
      );
      const result = parseDropshipPriorityResponse(response, payload.length);
      const displayMessage =
        result.status === 'success'
          ? 'Priorities updated successfully from CSV.'
          : result.message;

      setSubmitResult({
        status: result.status,
        updated_count: result.updatedCount,
        message: displayMessage,
        submitted_count: result.submittedCount,
        not_found: result.notFound,
      });
      sessionStorage.setItem('pickSequenceNeedsRefresh', 'true');
      setSnackbar({
        open: true,
        message: displayMessage,
        severity: result.severity,
      });

      if (result.shouldClearForm) {
        resetSelection();
      }
    } catch (err) {
      setSnackbar({
        open: true,
        message:
          err?.response?.data?.message ||
          err?.response?.data?.detail ||
          err?.message ||
          'Failed to update priorities.',
        severity: 'error',
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <DashboardLayout>
      <Box sx={{ p: 2 }}>
        <Typography variant="h5" sx={{ fontWeight: 700, color: 'primary.main', mb: 0.5 }}>
          Bulk Sales Order Priority
        </Typography>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 2, maxWidth: 720 }}>
          Upload a CSV with <strong>internal_id</strong> and <strong>priority</strong>. Rows are sent
          to the moment scheduler <strong>dropship-update-priority</strong> API.
        </Typography>

        <Alert severity="info" sx={{ mb: 2, maxWidth: 720 }}>
          CSV format: <strong>internal_id,priority</strong>
        </Alert>

        {submitResult && (
          <Alert
            severity={
              submitResult.status === 'success'
                ? 'success'
                : submitResult.status === 'warning'
                  ? 'warning'
                  : 'error'
            }
            sx={{ mb: 2, maxWidth: 720 }}
            onClose={() => setSubmitResult(null)}
          >
            <Typography variant="body2" sx={{ fontWeight: 600 }}>
              {submitResult.message}
            </Typography>
            {submitResult.status !== 'success' && (
              <Typography variant="caption" color="text.secondary" display="block" sx={{ mt: 0.5 }}>
                Status: {submitResult.status} · Processed: {submitResult.updated_count} of{' '}
                {submitResult.submitted_count} submitted
              </Typography>
            )}
            {submitResult.not_found?.length > 0 && (
              <Typography variant="caption" color="text.secondary" display="block" sx={{ mt: 0.5 }}>
                Not found: {submitResult.not_found.join(', ')}
              </Typography>
            )}
          </Alert>
        )}
        <Button variant="outlined" size="small" onClick={handleDownloadSampleCsv} sx={{ mb: 2 }}>
          Download sample CSV
        </Button>

        <Paper
          elevation={2}
          onDragOver={(e) => {
            e.preventDefault();
            setIsDragging(true);
          }}
          onDragLeave={(e) => {
            e.preventDefault();
            setIsDragging(false);
          }}
          onDrop={handleDrop}
          sx={{
            p: 3,
            mb: 2,
            border: '2px dashed',
            borderColor: isDragging ? 'primary.main' : 'grey.400',
            backgroundColor: isDragging ? 'rgba(25, 118, 210, 0.04)' : 'background.paper',
            textAlign: 'center',
            maxWidth: 720,
          }}
        >
          <UploadFileIcon sx={{ fontSize: 48, color: 'primary.main', mb: 1 }} />
          <Typography variant="h6" sx={{ mb: 1 }}>
            Drag and drop a CSV file here
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            or browse to select a file
          </Typography>
          <input
            ref={inputRef}
            type="file"
            accept=".csv,text/csv"
            style={{ display: 'none' }}
            onChange={handleInputChange}
          />
          <Button variant="contained" onClick={() => inputRef.current?.click()} disabled={isSubmitting}>
            Browse CSV
          </Button>
          {file && (
            <Box sx={{ mt: 2, display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 1 }}>
              <Typography variant="body2">{file.name}</Typography>
              <Tooltip title="Remove file">
                <IconButton size="small" onClick={resetSelection} disabled={isSubmitting}>
                  <DeleteIcon fontSize="small" />
                </IconButton>
              </Tooltip>
            </Box>
          )}
        </Paper>

        {parseWarnings.length > 0 && (
          <Alert severity="warning" sx={{ mb: 2, maxWidth: 720 }}>
            {parseWarnings.length} row(s) skipped:
            <Box component="ul" sx={{ mt: 1, mb: 0, pl: 2 }}>
              {parseWarnings.slice(0, 5).map((warning) => (
                <li key={warning}>
                  <Typography variant="body2">{warning}</Typography>
                </li>
              ))}
            </Box>
            {parseWarnings.length > 5 && (
              <Typography variant="caption" color="text.secondary">
                …and {parseWarnings.length - 5} more
              </Typography>
            )}
          </Alert>
        )}

        {previewRows.length > 0 && (
          <Paper elevation={1} sx={{ mb: 2, maxWidth: 720 }}>
            <Box sx={{ p: 2, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <Typography variant="h6">Preview ({previewRows.length} rows)</Typography>
              <Button
                variant="contained"
                startIcon={
                  isSubmitting ? <CircularProgress size={18} color="inherit" /> : <CloudUploadIcon />
                }
                onClick={handleSubmit}
                disabled={isSubmitting}
              >
                {isSubmitting ? 'Updating…' : 'Update priorities'}
              </Button>
            </Box>
            <TableContainer sx={{ maxHeight: 420 }}>
              <Table size="small" stickyHeader>
                <TableHead>
                  <TableRow>
                    <TableCell>internal_id</TableCell>
                    <TableCell align="right">Priority</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {previewRows.map((row) => (
                    <TableRow key={row.id} hover>
                      <TableCell>{row.internal_id}</TableCell>
                      <TableCell align="right">{row.priority}</TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          </Paper>
        )}

        <Snackbar
          open={snackbar.open}
          autoHideDuration={6000}
          onClose={() => setSnackbar((prev) => ({ ...prev, open: false }))}
          anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
        >
          <Alert
            onClose={() => setSnackbar((prev) => ({ ...prev, open: false }))}
            severity={snackbar.severity}
            sx={{ width: '100%' }}
          >
            {snackbar.message}
          </Alert>
        </Snackbar>
      </Box>
    </DashboardLayout>
  );
};

export default BulkSalesOrderPriority;
